import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { formatPrice } from 'utils/format';
import productCoverImage from 'utils/productCoverImage';
import { CloudinaryPicture, WIDTH_LADDERS } from 'components/rich';

import PdpIcon from '../product/pdpIcon';

/**
 * Product card of the redesigned pages (Pencil component "Product Card"):
 * image with optional badges, caption, meta line, price and CTA. Renders an
 * <a>, so wrap it in a Next link (e.g. ProductDetailLink), which passes href.
 * Replaces the shared ProductCard page by page.
 */
// Four cards per row on desktop, two on tablets, one on phones.
const DEFAULT_SIZES = '(min-width: 992px) 25vw, (min-width: 576px) 50vw, 100vw';

const CatalogProductCard = forwardRef(
  (
    { product, image, meta, badge, sale, ctaLabel, sizes = DEFAULT_SIZES, className, ...linkProps },
    ref,
  ) => {
    const disabled = product.config?.disabled === true;
    const hasPriceRange = product.prices.length > 1;
    // Bundles (e.g. print + rails) add prices but no size choice, so count the plain SKUs.
    const hasSizeChoice = product.options.length > 1;
    const price = formatPrice(product.prices[0], product.currency, true);
    const discountedPrice =
      sale?.discountedPriceFrom != null
        ? formatPrice(sale.discountedPriceFrom, product.currency, true)
        : null;
    const coverImage = productCoverImage(product);

    return (
      <a ref={ref} className={classNames('catalog-card', className)} {...linkProps}>
        <div className="catalog-card-image">
          <CloudinaryPicture
            prefix={image ? image.prefix : 'products/'}
            imageKey={image ? image.imageKey : coverImage?.key}
            widths={image ? image.widths : WIDTH_LADDERS.products}
            sizes={sizes}
            loading="lazy"
            className={disabled ? 'catalog-card-picture-disabled' : undefined}
          />
          {(badge || sale?.badge) && (
            <div className="catalog-card-badges">
              {badge && <span className="catalog-card-badge">{badge}</span>}
              {sale?.badge && (
                <span className="catalog-card-badge catalog-card-badge-sale coupon-reveal">
                  {sale.badge}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="catalog-card-body">
          <div>
            <h3 className="catalog-card-title">{product.name}</h3>
            {meta && <p className="catalog-card-meta">{meta}</p>}
          </div>

          <div className="catalog-card-footer">
            {disabled ? (
              <span className="catalog-card-soon">Coming Soon</span>
            ) : (
              <>
                {price && (
                  <div className="catalog-card-price">
                    {hasPriceRange && (
                      <span className="catalog-card-price-prefix">Starting at</span>
                    )}
                    {discountedPrice ? (
                      <span
                        className="catalog-card-price-row coupon-reveal"
                        role="img"
                        aria-label={`${discountedPrice} with coupon, was ${price}`}
                      >
                        <span
                          className="catalog-card-price-value catalog-card-price-sale"
                          aria-hidden="true"
                        >
                          {discountedPrice}
                        </span>
                        <s className="catalog-card-price-original" aria-hidden="true">
                          {price}
                        </s>
                      </span>
                    ) : (
                      <span className="catalog-card-price-value">{price}</span>
                    )}
                  </div>
                )}
                <span className="catalog-card-cta">
                  {ctaLabel ?? (hasSizeChoice ? 'Choose size' : 'Choose photo')}
                  <PdpIcon name="arrowRight" size={15} />
                </span>
              </>
            )}
          </div>
        </div>
      </a>
    );
  },
);

CatalogProductCard.displayName = 'CatalogProductCard';

CatalogProductCard.propTypes = {
  // Product from the data context (name, prices, currency, images, config).
  product: PropTypes.object.isRequired,
  // Overrides the product cover, e.g. a product-group category photo from categories/.
  image: PropTypes.shape({
    imageKey: PropTypes.string.isRequired,
    prefix: PropTypes.string,
    widths: PropTypes.arrayOf(PropTypes.number),
  }),
  // One-line summary under the caption, content `cardMeta`.
  meta: PropTypes.string,
  // Merchandising label on the image, content `badge`.
  badge: PropTypes.string,
  // Claimed-coupon sale treatment, as computed by `ecommerce/lib/coupon` couponSale().
  sale: PropTypes.shape({
    badge: PropTypes.string,
    discountedPriceFrom: PropTypes.number,
  }),
  // Defaults to "Choose size" for products with several SKUs, "Choose photo" otherwise.
  ctaLabel: PropTypes.string,
  // <img sizes> for the products/ width ladder, the caller knows the grid.
  sizes: PropTypes.string,
  className: PropTypes.string,
};

export default CatalogProductCard;
